import React, { FunctionComponent, memo, useMemo, useCallback, ReactNode, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { isEqual } from "lodash";

import {
  SiteConfig_siteConfig_algorithmsConf,
} from "graphql/generated";
import {
  Flex,
  Table,
  Tbody,
  Thead,
  Tr,
  useColorModeValue,
  Td,
  Text,
} from "@chakra-ui/react";

import { PlcTransmission } from "./types";

import ActivityIndicator from "components/activity-indicator";
import PlcControlReason from "./PlcControlReason";

import { PlcControlTransmission } from "./source/types";

import SourceGrid from "./source/SourceGrid";
import SourcePv from "./source/SourcePv";
import SourceBattery from "./source/SourceBattery";
import SourceChp from "./source/SourceChp";
import SourceHeating from "./source/SourceHeating";
import ControlUIContext from "./ControlUIContext";


interface IProps {
  algorithmsConf: SiteConfig_siteConfig_algorithmsConf;
  data: Record<string, any>;
}


const Control: FunctionComponent<IProps> = memo(function Control({
  algorithmsConf,
  data,
}) {
  const intl = useIntl();
  const [expandedReports, expandReports] = useState<boolean>(false);
  const [chainLength, setChainLength] = useState<number>(0);
  const plc: PlcTransmission = data.plc;
  const plcControl: PlcControlTransmission = data.plc;
  const hoursColor = useColorModeValue("white", "gray.950");
  const plcColor = useColorModeValue("gray.100", "gray.900");
  const plcCellColor = useMemo<string | undefined>(() => plc?.modes?.blocking ? plcColor : undefined, [plc, plcColor]);
  const plcHeaderColor = useMemo<string>(() => plc?.modes?.blocking ? "gray.500" : "enposol.500", [plc]);

  const yesLabel = useMemo(() => intl.formatMessage({
    id: "site__peak_shaving___value__boolean_yes",
    defaultMessage: "Y",
  }), [intl])

  const noLabel = useMemo(() => intl.formatMessage({
    id: "site__peak_shaving___value__boolean_no",
    defaultMessage: "N",
  }), [intl])

  const liveValueHint = useMemo(() => intl.formatMessage({
    id: "site__peak_shaving___value__plc__live",
    defaultMessage: "Measured value",
  }), [intl])

  const driverValueHint = useMemo(() => intl.formatMessage({
    id: "site__peak_shaving___value__plc__driver",
    defaultMessage: "Target control value",
  }), [intl])

  const value = useCallback((val: number | boolean): string => {
    if (val === true || val === false) return val ? yesLabel : noLabel;
    if (val === undefined) return "-";
    return Math.round(val).toString();
  }, [yesLabel, noLabel])

  const driver_value = useCallback((val: number | boolean): ReactNode => {
    return <>
      <Text as="span" title={driverValueHint} fontWeight="bold" background={plcCellColor} color={plcHeaderColor} p={0} borderRadius={10}>
        {value(val)}
      </Text>
    </>;
  }, [value, driverValueHint, plcHeaderColor, plcCellColor]);

  const live_value = useCallback((val: number | boolean): ReactNode => {
    return <>
      <Text as="span" title={liveValueHint} fontSize="s" p={0}>
        {value(val)}
      </Text>
    </>
  }, [value, liveValueHint]);

  const unit = useCallback((unit: string): ReactNode => {
    return <>
      <Text fontSize="x-small">
        [{unit}]
      </Text>
    </>
  }, []);

  useEffect(() => {
    if (!plcControl) { setChainLength(0); return; }
    const techs = [plcControl.grid, plcControl.pv, plcControl.battery, plcControl.chp, plcControl.heating];
    const lengths = techs
      .filter(Boolean)
      .map(tech => (tech!.reports ?? []).reduce((sum, report) => sum + report.results.length, 0));
    setChainLength(lengths.length ? Math.max(...lengths) : 0);
  }, [plcControl]);

  return (
    <>
      {algorithmsConf.uiVersion === "core" && (
        <ControlUIContext.Provider
          value={{
            expandedReports,
            expandReports,
            chainLength,
            setChainLength,
          }}
        >
        <Flex flexDirection="row" position="relative" pl={2}>
          <ActivityIndicator data={plc} position="absolute" left={0} top={0} color={"enposol.500"} />
          {plcControl && <>
            <SourceGrid data={plcControl.grid} />
            <SourcePv data={plcControl.pv} />
            <SourceBattery data={plcControl.battery} />
            <SourceChp data={plcControl.chp} />
            <SourceHeating data={plcControl.heating} />
          </>}
        </Flex>
        </ControlUIContext.Provider>
      )}
      {algorithmsConf.uiVersion === "default" &&
      <Table variant="unstyled" size="sm" mb={10} p={0}>
        <Thead>
          <Tr bg={plcHeaderColor} color={hoursColor} fontWeight="bold" verticalAlign="bottom">
            <Td colSpan={1} position="relative">
              <ActivityIndicator data={plc} position="absolute" left={0} top={0} />
              {intl.formatMessage({
                id: "site__peak_shaving__control",
                defaultMessage: "Control",
              })}
            </Td>
            <Td colSpan={1}>
              {intl.formatMessage({
                id: "site__peak_shaving__site",
                defaultMessage: "Site",
              })}
            </Td>
            <Td colSpan={1}>
              {intl.formatMessage({
                id: "site__peak_shaving__grid",
                defaultMessage: "Grid",
              })}
            </Td>
            <Td colSpan={4}>
              {intl.formatMessage({
                id: "site__peak_shaving__battery",
                defaultMessage: "Battery",
              })}
              <PlcControlReason reason={data.battery_control?.reason} timestamp={data.battery_control?.timestamp} />
            </Td>
            <Td colSpan={2}>
              {intl.formatMessage({
                id: "site__peak_shaving__pv",
                defaultMessage: "PV",
              })}
              <PlcControlReason reason={data.pv_control?.reason} timestamp={data.pv_control?.timestamp} />
            </Td>
            <Td colSpan={1}>
              {intl.formatMessage({
                id: "site__peak_shaving__chp",
                defaultMessage: "CHP",
              })}
              <PlcControlReason reason={data.chp_control?.reason} timestamp={data.chp_control?.timestamp} />
            </Td>
            <Td colSpan={2}>
              {intl.formatMessage({
                id: "site__peak_shaving__heating",
                defaultMessage: "Heating",
              })}
              <PlcControlReason reason={data.heating_control?.reason} timestamp={data.heating_control?.timestamp} />
            </Td>
            <Td colSpan={2}>
              {intl.formatMessage({
                id: "site__peak_shaving__furnace",
                defaultMessage: "Furnace",
              })}
            </Td>
          </Tr>
          <Tr bg={plcHeaderColor} color={hoursColor} verticalAlign="top">
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving___blocking",
                defaultMessage: "Blocked",
              })}
            </Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving___site_active_power",
                defaultMessage: "Power",
              })}
              {unit("kW")}
            </Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__grid_active_power",
                defaultMessage: "Power",
              })}
              {unit("kW")}
            </Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__battery_soc",
                defaultMessage: "SoC",
              })}
              {unit("%")}</Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__battery_discharge",
                defaultMessage: "Discharge",
              })}
              {unit("kW")}</Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__battery_control",
                defaultMessage: "Control",
              })}
            </Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__battery_active_power",
                defaultMessage: "Power",
              })}
              {unit("kW")}</Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__pv_active_power",
                defaultMessage: "Power",
              })}
              {unit("kW")}</Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__pv_power_throttled_to",
                defaultMessage: "Throttled to",
              })}
              {unit("%")}</Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__chp_active_power",
                defaultMessage: "Power",
              })}
              {unit("kW")}</Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__heating_active_power",
                defaultMessage: "Power",
              })}
              {unit("kW")}</Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__heating_temperature",
                defaultMessage: "Temperature",
              })}
              {unit("°C")}</Td>
            <Td>
              {intl.formatMessage({
                id: "site__peak_shaving__furnace_active_power",
                defaultMessage: "Power",
              })}
              {unit("kW")}</Td>
          </Tr>
        </Thead>
        <Tbody>
          <Tr bg={plcCellColor}>
            <Td fontSize="1xl">{live_value(plc?.modes.blocking)}</Td>
            <Td fontSize="1xl">{live_value(plc?.site.live_active_power)}</Td>
            <Td fontSize="1xl">{live_value(plc?.grid.live_active_power)}</Td>
            <Td fontSize="1xl">{live_value(plc?.battery.live_soc)}</Td>
            <Td fontSize="1xl">{live_value(plc?.battery.live_discharge)} {driver_value(plc?.battery.discharge)}</Td>
            <Td fontSize="1xl">{live_value(plc?.battery.live_control)} {driver_value(plc?.battery.control)}</Td>
            <Td fontSize="1xl">{live_value(plc?.battery.live_active_power)}</Td>
            <Td fontSize="1xl">{live_value(plc?.pv.live_active_power)}</Td>
            <Td fontSize="1xl">{live_value(plc?.pv.live_power_throttle)} {driver_value(plc?.pv.power_throttle)}</Td>
            <Td fontSize="1xl">{driver_value(plc?.chp.active_power)}</Td>
            <Td fontSize="1xl">{driver_value(plc?.heating.active_power)}</Td>
            <Td fontSize="1xl">{live_value(plc?.heating.live_temperature)}</Td>
            <Td fontSize="1xl">{live_value(plc?.furnace.live_active_power)}</Td>
          </Tr>
        </Tbody>
      </Table>}
    </>
  );
}, isEqual);

export default Control;
